import api from '@/lib/axios';

export interface BoardFile {
  id: string;
  notionBoardId: string;
  name: string;
  referenceLabel?: string | null;
  mimeType?: string | null;
  size?: number | null;
  uploadedBy?: string | null;
  createdAt: string;
  uploadedByUser?: {
    id: string;
    email: string;
  } | null;
}

export const kanbanService = {
  getBoards: async () => {
    const { data } = await api.get('/notion/boards');
    return data;
  },

  getMyBoards: async () => {
    const { data } = await api.get('/notion/my-boards');
    return data;
  },

  /** Boards atribuídos ao usuário para o Notebook (apenas quem tem NOTEBOOK_VIEW) */
  getBoardsForNotebook: async () => {
    const { data } = await api.get('/notion/boards/for-notebook');
    return data;
  },

  getBoardTasks: async (boardId: string) => {
    const { data } = await api.get(`/notion/boards/${boardId}/tasks`);
    return data;
  },

  getSyncStatus: async () => {
    const { data } = await api.get('/notion/sync-status');
    return data;
  },

  triggerSync: async () => {
    const { data } = await api.post('/notion/sync/trigger');
    return data;
  },

  // Admin endpoints
  getConfig: async () => {
    const { data } = await api.get('/notion/config');
    return data;
  },

  saveConfig: async (config: any) => {
    const { data } = await api.post('/notion/config', config);
    return data;
  },

  testConnection: async (token?: string) => {
    const { data } = await api.post('/notion/config/test-connection', {
      notionAccessToken: token,
    });
    return data;
  },

  createBoard: async (board: any) => {
    const { data } = await api.post('/notion/boards', board);
    return data;
  },

  updateBoard: async (boardId: string, board: any) => {
    const { data } = await api.put(`/notion/boards/${boardId}`, board);
    return data;
  },

  deleteBoard: async (boardId: string) => {
    const { data } = await api.delete(`/notion/boards/${boardId}`);
    return data;
  },

  getDatabaseProperties: async (databaseId: string) => {
    const { data } = await api.get(`/notion/databases/${databaseId}/properties`);
    return data;
  },

  getMyTasks: async () => {
    // Backend retorna todas as tarefas do usuário, filtro de boards é feito no frontend
    const { data } = await api.get('/notion/my-tasks');
    return data; // Retorna { tasks, lastUpdate, lastLocalUpdate }
  },

  refreshMyTasks: async () => {
    // Atualiza as tarefas do Redis sem forçar sincronização no Notion
    const { data } = await api.post('/notion/my-tasks/refresh');
    return data; // Retorna { tasks, lastUpdate, lastLocalUpdate }
  },

  getTaskDetails: async (pageId: string) => {
    // Busca detalhes completos de uma task do Notion incluindo conteúdo/blocks
    const { data } = await api.get(`/notion/tasks/${pageId}/details`);
    return data;
  },

  diagnoseTaskSync: async (notionPageId: string) => {
    const { data } = await api.post('/notion/admin/debug/task-diagnosis', { notionPageId });
    return data;
  },

  // Admin sync endpoints
  getGlobalSyncStatus: async () => {
    const { data } = await api.get('/notion/sync/global-status');
    return data;
  },

  triggerGlobalSync: async () => {
    const { data } = await api.post('/notion/sync/trigger-global');
    return data;
  },

  resetGlobalStaleSync: async () => {
    const { data } = await api.post('/notion/sync/reset-global-stale');
    return data;
  },

  triggerTenantSync: async (tenantId: string) => {
    const { data } = await api.post(`/notion/sync/trigger-tenant/${tenantId}`);
    return data;
  },

  // Board files (arquivos do quadro / cliente)
  getBoardFiles: async (boardId: string) => {
    const { data } = await api.get<BoardFile[]>(`/notion/boards/${boardId}/files`);
    return data;
  },
  uploadBoardFile: async (
    boardId: string,
    file: File,
    referenceLabel?: string,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (referenceLabel?.trim()) {
      formData.append('referenceLabel', referenceLabel.trim());
    }
    const { data } = await api.post(
      `/notion/boards/${boardId}/files`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return data;
  },
  getBoardFileDownloadUrl: async (boardId: string, fileId: string) => {
    const { data } = await api.get(
      `/notion/boards/${boardId}/files/${fileId}/download-url`,
    );
    return data as { url: string };
  },
  deleteBoardFile: async (boardId: string, fileId: string) => {
    await api.delete(`/notion/boards/${boardId}/files/${fileId}`);
  },

  // Board analysis (NotebookLM-style com Gemini)
  runBoardAnalysis: async (boardId: string, type?: string) => {
    const { data } = await api.post(
      `/notion/boards/${boardId}/analysis`,
      type != null ? { type } : {},
    );
    return data;
  },
  getBoardAnalyses: async (boardId: string) => {
    const { data } = await api.get(`/notion/boards/${boardId}/analysis`);
    return data;
  },

  // Usuários atribuídos ao Notebook (quem pode ver este board no Notebook)
  getNotebookAssignments: async (boardId: string) => {
    const { data } = await api.get(
      `/notion/boards/${boardId}/notebook-users`,
    );
    return data as { userId: string; createdAt: string }[];
  },
  addNotebookAssignment: async (boardId: string, userId: string) => {
    const { data } = await api.post(
      `/notion/boards/${boardId}/notebook-users`,
      { userId },
    );
    return data;
  },
  removeNotebookAssignment: async (boardId: string, userId: string) => {
    await api.delete(
      `/notion/boards/${boardId}/notebook-users/${userId}`,
    );
  },
};
